import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NfcChat.css';
import coinIcon from '../assets/icon/coinIcon.png';
import userIcon from '../assets/icon/userIcon.png';
import deepReadingIcon from '../assets/icon/deepreadingIcon.png';
import fastReadingIcon from '../assets/icon/fastReadingIcon.png';
import readingCoinIcon from '../assets/icon/readingCoinIcon.png';
import tarotBackSide from '../assets/picture/tarotCards/000.png';
import ichingBackSide from '../assets/picture/IChingCard.png';
import { chat, getUserInfo, updateDcoin } from '../functions/index';
import { getToken, getUsername, isCurrentTokenExpired, decodeToken } from '../utils/auth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedTarotMeaning, getLocalizedIChingMeaning } from '../utils/cardMeaningsHelper';

interface ChatMessage {
    role: 'user' | 'ai';
    content: string;
    isStreamed?: boolean;
}

// Load Tarot images context
const tarotImagesContext = require.context('../assets/picture/tarotCards', false, /\.png$/);
const tarotImageMap: { [key: string]: string } = {};
tarotImagesContext.keys().forEach((item: string) => {
    const cardId = item.replace('./', '').replace('.png', '');
    if (cardId !== '000') {
        tarotImageMap[cardId] = tarotImagesContext(item);
    }
});

// Load IChing images context
const ichingImagesContext = require.context('../assets/picture/IChing', false, /\.png$/);
const ichingImageMap: { [key: string]: string } = {};
ichingImagesContext.keys().forEach((item: string) => {
    const cardId = item.replace('./', '').replace('.png', '');
    if (cardId !== '000') {
        ichingImageMap[cardId] = ichingImagesContext(item);
    }
});

const NfcChat = () => {
    const { cardType } = useParams<{ cardType: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { cards } = location.state || {}; // Expect cards array from previous page

    // Determine config based on cardType
    const isTarot = cardType === 'tarot';
    const isIChing = cardType === 'iching';

    // Default to first card if available
    const card = cards && cards[0] ? cards[0] : { cardId: '000', cardName: 'Unknown', position: 'upright' };

    // Select image map and fallback
    let cardImage;
    if (isTarot) {
        cardImage = tarotImageMap[card.cardId] || tarotBackSide;
    } else {
        cardImage = ichingImageMap[card.cardId] || ichingBackSide;
    }

    const isReversed = isTarot && card.position === 'reversed';
    const aiRole = isTarot ? "塔罗牌占卜师" : "易经占卜师";

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [readingType, setReadingType] = useState<'deep' | 'fast'>('deep');
    const [coinBalance, setCoinBalance] = useState<number>(0);
    const [username, setUsername] = useState<string>('');

    const { language, setLanguage, t, showLanguageModal, setShowLanguageModal } = useLanguage();

    // Ref for auto-scrolling to bottom
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when messages change
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Initial greeting
    useEffect(() => {
        if (messages.length === 0) {
            let initialMessage = '';
            if (isTarot) {
                initialMessage = t.tarotGreeting(card.cardName, isReversed);
            } else {
                initialMessage = t.ichingGreeting(card.cardName);
            }
            setMessages([{ role: 'ai', content: initialMessage }]);
        }
    }, [messages.length, isTarot, isReversed, card.cardName, language]);

    // Fetch coin balance and username
    useEffect(() => {
        const fetchUserInfo = async () => {
            const token = getToken();

            if (token && !isCurrentTokenExpired()) {
                try {
                    const userInfo = await getUserInfo(token);
                    if (userInfo && typeof userInfo.coin === 'number') {
                        setCoinBalance(userInfo.coin);
                    }
                    if (userInfo && userInfo.username) {
                        setUsername(userInfo.username);
                    }
                } catch (error) {
                    console.error('Failed to fetch user info:', error);
                }
            }
        };

        fetchUserInfo();
    }, []);

    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;

        const token = getToken();
        if (!token || isCurrentTokenExpired()) {
            // Keep state for return
            navigate('/authPage', { state: { ...location.state, from: location.pathname } });
            return;
        }

        const userMsg = inputMessage;
        setMessages(prev => [...prev, { role: 'user', content: userMsg, isStreamed: true }]);
        // Add empty AI message that will be filled by streaming
        setMessages(prev => [...prev, { role: 'ai', content: '', isStreamed: true }]);
        setInputMessage('');
        setIsLoading(true);

        const cost = readingType === 'deep' ? -20 : -10;
        const requiredCoins = Math.abs(cost);

        if (coinBalance < requiredCoins) {
            setMessages(prev => [...prev, { role: 'ai', content: t.insufficientCoins }]);
            setIsLoading(false);
            return;
        }

        // Deduct coins logic (omitted complex user checks for brevity, assuming happy path from original)
        let targetUsername = username;
        if (!targetUsername) {
            const tokenPayload = decodeToken(token);
            targetUsername = tokenPayload?.username || '';
        }

        if (targetUsername) {
            try {
                const dcoinResponse = await updateDcoin(token, targetUsername, cost);
                // Update local balance from response logic similar to original file
                let newBalance = null;
                if (dcoinResponse && typeof dcoinResponse.coin === 'number') {
                    newBalance = dcoinResponse.coin;
                } else if (dcoinResponse && dcoinResponse.user && typeof dcoinResponse.user.coin === 'number') {
                    newBalance = dcoinResponse.user.coin;
                } else if (dcoinResponse && dcoinResponse.data && typeof dcoinResponse.data.coin === 'number') {
                    newBalance = dcoinResponse.data.coin;
                } else {
                    const userInfo = await getUserInfo(token);
                    if (userInfo && typeof userInfo.coin === 'number') {
                        newBalance = userInfo.coin;
                    }
                }
                if (newBalance !== null) setCoinBalance(newBalance);
            } catch (dcoinError) {
                console.error('Failed to update dcoin:', dcoinError);
                setMessages(prev => [...prev, { role: 'ai', content: t.transactionFailed }]);
                setIsLoading(false);
                return;
            }
        }

        try {
            let prompt = "";
            const currentCardInfo = isTarot
                ? `${card.cardName} (${card.position})`
                : `hexagram ${card.cardName}`;

            const contextType = isTarot ? 'mystical tarot reader' : 'I Ching sage';

            // Construct base prompt
            if (messages.length === 1) {
                if (readingType === 'fast') {
                    prompt = t.promptContextFast(currentCardInfo, userMsg);
                } else {
                    prompt = t.promptContextDeep(currentCardInfo, userMsg, isTarot ? 'Tarot' : 'I Ching');
                }
            } else {
                prompt = t.promptContextHistory(currentCardInfo, userMsg, contextType, readingType === 'fast');
            }

            // Append language instruction
            prompt += t.languagePrompt;

            const reader = await chat(token, prompt, aiRole);
            const decoder = new TextDecoder();
            let buf = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                // console.log('Received chunk:', chunk);
                buf += chunk;

                // Try parsing as single line SSE or newline-separated chunks
                const parts = buf.split(/\r?\n/);
                buf = parts.pop() || ''; // Keep incomplete line in buffer

                for (const line of parts) {
                    // console.log('Processing line:', line);
                    if (!line.trim()) continue;

                    let raw = line;
                    if (raw.startsWith('data:')) {
                        raw = raw.slice(5).trim();
                    }

                    if (!raw || raw === '[DONE]') continue;

                    let obj = null;
                    let content = '';

                    try {
                        obj = JSON.parse(raw);
                    } catch (e) {
                        // Not JSON, treat as raw text
                        content = raw;
                    }

                    if (obj) {
                        if (obj.choices && obj.choices[0]) {
                            const delta = obj.choices[0].delta || {};
                            if (typeof delta.content === 'string') content = delta.content;
                            else if (typeof obj.choices[0].text === 'string') content = obj.choices[0].text;
                        } else if (typeof obj.content === 'string') {
                            content = obj.content;
                        }
                    }

                    if (content) {
                        setMessages(prev => {
                            const newMessages = [...prev];
                            const lastIndex = newMessages.length - 1;
                            if (lastIndex >= 0 && newMessages[lastIndex].role === 'ai') {
                                const currentContent = newMessages[lastIndex].content;
                                const updatedMsg = {
                                    ...newMessages[lastIndex],
                                    content: currentContent + content,
                                    isStreamed: true
                                };
                                newMessages[lastIndex] = updatedMsg;
                            }
                            return newMessages;
                        });
                    }
                }
            }
        } catch (error) {
            console.error("Chat failed:", error);
            setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg.role === 'ai' && !lastMsg.content) {
                    const newMessages = [...prev];
                    newMessages[prev.length - 1] = { role: 'ai', content: t.connectionInterrupted, isStreamed: true };
                    return newMessages;
                }
                return prev;
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const toggleReadingType = () => {
        setReadingType(prev => prev === 'deep' ? 'fast' : 'deep');
    };

    const handleRefreshQuestion = () => {
        const randomIndex = Math.floor(Math.random() * questions.length);
        setInputMessage(questions[randomIndex]);
    };

    const handleUserClick = () => {
        navigate('/userCenter');
    };

    const isLoggedIn = !!getToken() && !isCurrentTokenExpired();

    if (!isTarot && !isIChing) {
        return <div className="nfc-chat-page">{t.invalidCardType}</div>;
    }

    const questions = isTarot ? t.tarotQuestions : t.ichingQuestions;

    return (
        <div className="nfc-chat-page">
            {/* Header */}
            <div className="nfc-chat-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="nfc-pill coin-balance">
                        <img src={coinIcon} alt="Coin" />
                        <span>{coinBalance}</span>
                    </div>
                </div>

                <div className="header-right">
                    {isLoggedIn ? (
                        <button className='user-button' onClick={handleUserClick}>
                            <div className="nfc-pill user-logged-in">
                                <img src={userIcon} alt="User" />
                                {username && <span>{username}</span>}
                            </div>
                        </button>
                    ) : (
                        <button className='user-button' onClick={() => navigate('/authPage')}>
                            <div className="nfc-pill user-logged-in">
                                <img src={userIcon} alt="Login" />
                                <span>{t.login}</span>
                            </div>
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className={`nfc-chat-content ${isExpanded ? 'expanded' : ''}`}>
                {/* Card Display Area - Click to Expand */}
                <div className={`card-display-area ${isExpanded ? 'expanded' : ''}`} onClick={!isExpanded ? toggleExpand : undefined}>
                    {isExpanded && (
                        <button className="close-expanded-btn" onClick={(e) => { e.stopPropagation(); toggleExpand(); }}>×</button>
                    )}

                    <img
                        src={cardImage}
                        alt={card.cardName}
                        className={`${isReversed ? 'reversed' : ''} ${isReversed ? 'reversed-glow' : 'upright-glow'}`}
                    />

                    {!isExpanded ? (
                        <div className={`card-label ${isReversed ? 'reversed' : 'upright'}`}>
                            {isReversed ? t.reversed : (isTarot ? t.upright : t.hexagram)}
                        </div>
                    ) : (
                        <div className="card-details">
                            <h2 className="card-title">{card.cardName}</h2>
                            <p className={`card-subtitle ${isReversed ? 'reversed' : 'upright'}`}>
                                {isTarot ? (isReversed ? t.reversedMeaning : t.uprightMeaning) : t.hexagramMeaning}
                            </p>

                            {(() => {
                                // Get localized card meaning based on current language
                                const cardMeaning = isTarot
                                    ? getLocalizedTarotMeaning(card.cardId, language)
                                    : getLocalizedIChingMeaning(card.cardId, language);
                                const meaning = cardMeaning ? (isReversed ? cardMeaning.reversed : cardMeaning.upright) : null;

                                return (
                                    <>
                                        <div className="card-meaning-text">
                                            {cardMeaning ? (
                                                <p>{cardMeaning.introduction}</p>
                                            ) : (
                                                isTarot ? t.tarotStatic(card.cardName, isReversed) : t.ichingStatic
                                            )}
                                        </div>

                                        <div className="card-general-meaning">
                                            {meaning && (
                                                <p>{meaning.generalMeaning}</p>
                                            )}
                                        </div>

                                        <div className="card-keywords">
                                            <h4>{t.coreKeywords}</h4>
                                            <ul>
                                                {meaning && meaning.keywords.length > 0 ? (
                                                    meaning.keywords.slice(0, 6).map((keyword, idx) => (
                                                        <li key={idx}>{keyword}</li>
                                                    ))
                                                ) : (
                                                    <>
                                                        <li>{t.keyword1}</li>
                                                        <li>{t.keyword2}</li>
                                                        <li>{t.keyword3}</li>
                                                    </>
                                                )}
                                            </ul>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>

                {/* Chat Messages Area */}
                {!isExpanded && (
                    <>
                        <div className="chat-messages-area">
                            {messages.map((msg, index) => (
                                <div key={index} className={`message ${msg.role}`}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Chat Input Area */}
                        <div className="chat-input-container">
                            <div className="input-row">
                                <input
                                    type="text"
                                    className="chat-input"
                                    placeholder={isTarot ? t.inputPlaceholderTarot : t.inputPlaceholderIChing}
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                />
                                <button className="refresh-btn" onClick={handleRefreshQuestion}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                                    </svg>
                                </button>
                            </div>

                            <div className="controls-row">
                                <button
                                    className={`reading-type-toggle ${readingType === 'fast' ? 'fast' : ''}`}
                                    onClick={toggleReadingType}
                                >
                                    <img
                                        src={readingType === 'deep' ? deepReadingIcon : fastReadingIcon}
                                        alt={readingType === 'deep' ? t.deepReading : t.fastReading}
                                    />
                                    {readingType === 'deep' ? t.deepReading : t.fastReading}
                                </button>

                                <div className="right-controls">
                                    <div className="coin-display">
                                        <img src={readingCoinIcon} alt="Reading Coin" />
                                        <span>{readingType === 'deep' ? 20 : 10}</span>
                                    </div>

                                    <button className="send-btn" onClick={handleSendMessage}>
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default NfcChat;